/**
 * THE OFFICE'S VIEW OF ONE VISIT: the visit, the household and the standing arrangement, then the
 * change panel and every change recorded so far. Which panel stands in the change column, the
 * waiting change, the move form or a sentence saying neither, is chosen here.
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CADENCE_LABELS,
  PRICE_SOURCE_LABELS,
  SERVICE_LINE_LABELS,
  VISIT_KIND_LABELS,
  humanise,
} from '@fk/shared';
import { ApiError, draftInvoice, type Me, type OfficeVisitDetail } from '../../api.ts';
import { Card, Detail, Pill, ScreenTitle } from '../../components/AppShell.tsx';
import { CallLink, MailLink } from '../../components/CallLink.tsx';
import { Button } from '../../components/Field.tsx';
import { ErrorNotice, SavedNotice } from '../../components/Notice.tsx';
import { GapPill, NotToldPill, VisitStatusPill } from '../../components/StatusPills.tsx';
import {
  NobodyAssigned,
  declinedWords,
  needsACleaner,
  onTheCrew,
} from '../../components/VisitBits.tsx';
import {
  STATE,
  formatLocalWindow,
  formatWindow,
  longDay,
  money,
  weekdayName,
} from '../../format.ts';
import { CallOffCard } from './CallOffCard.tsx';
import { ChangeCard } from './ChangeCard.tsx';
import { MoveFlow, type CallOffChoiceStart } from './MoveFlow.tsx';
import { SheetButton, VisitFacts } from './VisitFacts.tsx';
import { WaitingPanel } from './WaitingPanel.tsx';
import { PAIR, reminderLines, waitingChange, type Outcome } from './shared.ts';

/**
 * The office drafts the invoice from a finished visit, here, because the visit is the only thing an
 * invoice can be drafted from. The lines come from the visit's own agreed figures; nothing is typed.
 */
function BillVisit({ visitId }: { visitId: string }) {
  const navigate = useNavigate();
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function draft() {
    setBusy(true);
    setProblem(null);
    try {
      const answer = await draftInvoice(visitId);
      navigate(`/invoices/${answer.id}`);
    } catch (thrown) {
      setProblem(thrown instanceof ApiError ? thrown.message : 'The invoice was not drafted.');
      setBusy(false);
    }
  }

  return (
    <Card className="mb-4 md:mb-0">
      <h2 className="font-serif text-lg">Bill it</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        The visit is done, so an invoice can be drafted from it. The published terms say the invoice
        goes within two days of the work, and the nightly check holds us to it.
      </p>
      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not drafted" message={problem} />
        </div>
      ) : null}
      <Button onClick={draft} disabled={busy} full>
        {busy ? 'Drafting' : 'Draft the invoice'}
      </Button>
    </Card>
  );
}

/**
 * THE VISIT IS BILLED (ops #61). Once its invoice stands, the office is shown that invoice and the
 * way to it, rather than offered a draft the server can only refuse `already_invoiced`. A voided
 * invoice does not stand, so a visit whose invoice was written off is offered "Bill it" again, as
 * the server allows. Hiding the draft is the screen agreeing with the server, never the control.
 */
function VisitInvoice({ invoiceId }: { invoiceId: string }) {
  return (
    <Card className="mb-4 md:mb-0">
      <h2 className="font-serif text-lg">Billed</h2>
      <p className="mt-1 mb-3 text-sm text-navy/70">
        This visit has its invoice, so there is no second one to draft. Where it stands and what has
        been paid against it are on the invoice.
      </p>
      <Link
        to={`/invoices/${invoiceId}`}
        className="inline-flex min-h-11 desk:min-h-9 w-full items-center justify-center rounded-lg border border-navy/25 bg-parchment px-4 text-base font-medium"
      >
        Open the invoice
      </Link>
    </Card>
  );
}

/** How a line about one channel is named above its pill. */
const BY_CHANNEL = { text: 'By text', email: 'By email' } as const;

/**
 * THE REMINDER BEFORE THE CLEAN, under the visit (ops #78): where it stands, or when the next is due,
 * or why none went, with the words she gets once one is written. By text and, since ops #76 O6, by
 * email: one line where both say the same or are due alike, "by text and by email", and otherwise a
 * line for each, named, stacked so a phone reads each whole. `reminderLines` reads it all; this only
 * draws it, and draws nothing from a Worker older than the field. Nothing to press.
 */
function Reminder({ detail, me }: { detail: OfficeVisitDetail; me: Me }) {
  const lines = reminderLines(detail, me.smsGoLive, me.emailGoLive, Date.now());
  if (!lines) return null;
  return (
    <div className="mt-2 border-t border-navy/10 pt-2" data-reminder>
      <p className="text-xs tracking-wide text-navy/55 uppercase">Reminder</p>
      {lines.map(({ channel, line }) => (
        <div key={channel ?? 'both'} className="mt-1">
          {channel ? (
            <p className="text-xs font-medium text-navy/70">{BY_CHANNEL[channel]}</p>
          ) : null}
          <p className="text-sm">
            <Pill>{line.label}</Pill> <span>{line.sentence}</span>
          </p>
          {line.body ? (
            <p className="mt-2 rounded-lg border border-navy/10 bg-cream px-3 py-2 text-sm whitespace-pre-line">
              {line.body}
            </p>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/**
 * WHAT THEY CHECK FIRST, PHOTOGRAPHED (ops #86 phase 4, `docs/plan-coach-review.md` section 7): the
 * household's own lines under the visit, "2 of 3 photographed", and beside each line whether a photo of
 * the visit stands for it, so the office knows before it presses Your home is ready. While the
 * household does not allow photos the lines stand with no marks and one sentence says why. The server
 * decided all of it and counts nothing it would not serve; this draws, and draws nothing from a Worker
 * older than the field.
 */
function ChecksFirst({ detail }: { detail: OfficeVisitDetail }) {
  const checks = detail.checksFirst;
  if (!checks || checks.lines.length === 0) return null;
  return (
    <div className="mt-2 border-t border-navy/10 pt-2" data-checks-first>
      <p className="text-xs tracking-wide text-navy/55 uppercase">They check first</p>
      <p className="mt-1 text-sm">
        {checks.photosAllowed
          ? `${checks.photographed} of ${checks.lines.length} photographed`
          : 'This household does not allow photos now, so no photo is shown or counted.'}
      </p>
      {checks.from === 'home' ? (
        <p className="text-xs text-navy/60">As the home says them; the sheet is not opened yet.</p>
      ) : null}
      <ul className="mt-1 space-y-1">
        {checks.lines.map((one, i) => (
          <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
            <span>{one.line}</span>
            {checks.photosAllowed ? (
              <Pill>{one.photographed ? 'Photographed' : 'No photo'}</Pill>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

const OPEN_STATUSES = new Set(['planned', 'confirmed', 'no_access']);

export function OfficeView({
  me,
  detail,
  outcome,
  onChanged,
  onReload,
}: {
  me: Me;
  detail: OfficeVisitDetail;
  outcome: Outcome | null;
  onChanged: (outcome: Outcome) => void;
  onReload: () => void;
}) {
  const { visit, cleaners, agreement, series, home, client, contacts, changes } = detail;
  const declined = cleaners.filter((cleaner) => cleaner.status === 'declined');
  const waiting = waitingChange(changes);
  // A cleaner who cannot come (ops #85): while the client has not chosen, the move flow opens only
  // from one of the three choices, carrying the call-off, since the server refuses any other change.
  const callOff = detail.callOff ?? null;
  const callOffOpen = callOff !== null && callOff.choice === null;
  const [choosing, setChoosing] = useState<CallOffChoiceStart | null>(null);

  return (
    <div>
      <ScreenTitle
        title={client.displayName}
        subtitle={`${longDay(visit.scheduledDate)}, ${formatWindow(visit.windowStart, visit.windowEnd) ?? 'no arrival window set'}`}
      />

      <div className="mb-4 md:mb-5 md:flex md:items-center md:justify-between md:gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <VisitStatusPill status={visit.status} />
          <Pill>{VISIT_KIND_LABELS[visit.kind]}</Pill>
          {waiting ? <NotToldPill /> : null}
          {needsACleaner(cleaners) ? <GapPill>Needs a cleaner</GapPill> : null}
        </div>
        <Link
          to={`/visits/${visit.id}/sheet`}
          className="hidden shrink-0 min-h-11 desk:min-h-9 items-center rounded-lg border border-navy/25 bg-parchment px-4 text-base font-medium md:inline-flex"
        >
          Open the job sheet
        </Link>
      </div>

      {outcome ? (
        <div className="mb-4">
          {outcome.tone === 'saved' ? (
            <SavedNotice>{outcome.text}</SavedNotice>
          ) : (
            <p
              className={`rounded-lg border px-3 py-2 text-sm ${
                outcome.tone === 'refused'
                  ? 'border-coral/50 border-l-4 border-l-coral bg-parchment'
                  : 'border-gold/40 bg-gold/15'
              }`}
              role="status"
            >
              {outcome.text}
            </p>
          )}
        </div>
      ) : null}

      {/* TWO COLUMNS ONLY FROM XL (ops #56). The change column is 520px, and at md the rail and
          the page padding leave 472, so beside it the visit was a column one word wide and the
          page scrolled sideways. Below xl the three blocks stand in one column, in the phone's
          order: the visit, the change, then what has changed. */}
      <div className="md:grid md:grid-cols-1 md:items-start md:gap-6 xl:grid-cols-[minmax(0,1fr)_520px]">
        {/* The visit itself, on the left of the desk and first on the phone. */}
        <div className="min-w-0 md:flex md:flex-col md:gap-4 xl:col-start-1 xl:row-start-1">
          <div className="md:hidden">
            <SheetButton visitId={visit.id} />
          </div>

          {detail.invoiceId ? (
            <VisitInvoice invoiceId={detail.invoiceId} />
          ) : visit.status === 'done' && !visit.askNothing ? (
            <BillVisit visitId={visit.id} />
          ) : null}

          <Card className="mb-4 md:mb-0">
            <h2 className="mb-2 font-serif text-lg">The visit</h2>
            <dl className={PAIR}>
              <VisitFacts visit={visit} />
              <Detail
                label="Price"
                value={
                  visit.priceSource === 'missed_visit_no_charge'
                    ? `${money(0)}: missed without notice, the published policy`
                    : visit.priceCents !== undefined
                      ? `${money(visit.priceCents)}${
                          visit.priceSource && visit.priceSource !== 'card'
                            ? `, ${PRICE_SOURCE_LABELS[visit.priceSource].toLowerCase()}`
                            : ' from the published card'
                        }`
                      : null
                }
              />
              <div className="py-1">
                <dt className="text-xs tracking-wide text-navy/55 uppercase">Who is on it</dt>
                <dd>
                  {cleaners.length === 0 ? (
                    <NobodyAssigned />
                  ) : onTheCrew(cleaners).length === 0 ? (
                    <p className="text-sm text-coral">
                      Needs a cleaner. Put somebody on it, or them back, through Change this visit.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {onTheCrew(cleaners).map((cleaner) => (
                        <li key={cleaner.id} className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{cleaner.displayName}</span>
                          {cleaner.phoneE164 ? <CallLink phoneE164={cleaner.phoneE164} /> : null}
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Who declined in the app, when and why (ops #74 item 5): beside the crew, never
                      in it, and the office's alone. */}
                  {declined.length > 0 ? (
                    <ul className="mt-2 space-y-1" data-declined>
                      {declined.map((cleaner) => (
                        <li key={cleaner.id} className="text-sm text-navy/75">
                          {declinedWords(cleaner)}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </dd>
              </div>
            </dl>
            <Reminder detail={detail} me={me} />
            <ChecksFirst detail={detail} />
          </Card>

          <Card className="mb-4 md:mb-0">
            <h2 className="mb-2 font-serif text-lg">The household</h2>
            <div className={PAIR}>
              <div className="min-w-0">
                <Link
                  to={`/clients/${client.id}`}
                  className="inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium underline"
                >
                  {client.displayName}
                </Link>
                <dl>
                  <Detail
                    label="Home"
                    value={[
                      home.label,
                      home.addressLine1,
                      home.addressLine2,
                      `${home.town}, ${STATE} ${home.postalCode}`,
                    ]
                      .filter(Boolean)
                      .join('\n')}
                  />
                </dl>
              </div>

              <div className="min-w-0">
                <p className="mt-2 text-xs tracking-wide text-navy/55 uppercase md:mt-1">
                  Who to tell
                </p>
                {contacts.length === 0 ? (
                  <p className="mt-1 text-sm text-coral">
                    Nobody is on file for this household. There is nobody to tell when this visit
                    moves.
                  </p>
                ) : (
                  <ul className="mt-1 space-y-2">
                    {contacts.map((contact) => (
                      <li key={contact.id}>
                        <p className="text-sm">
                          <span className="font-medium">{contact.displayName}</span>
                          <span className="ml-2 text-navy/60">{humanise(contact.role)}</span>
                        </p>
                        <div className="mt-1 flex flex-wrap gap-2">
                          <CallLink phoneE164={contact.phoneE164} />
                          <MailLink email={contact.email} />
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Card>

          <Card className="mb-4 md:mb-0">
            <h2 className="mb-2 font-serif text-lg">The standing arrangement</h2>
            <dl className={PAIR}>
              <Detail label="Service" value={SERVICE_LINE_LABELS[agreement.serviceLine]} />
              <Detail label="Cadence" value={CADENCE_LABELS[agreement.cadence]} />
              <Detail label="Agreed price" value={money(agreement.priceCents)} />
              <Detail
                label="Usual slot"
                value={
                  series
                    ? `${weekdayName(series.anchorDate)}s, ${formatLocalWindow(series.windowStartLocal, series.windowEndLocal)}`
                    : 'A one-off, with no standing schedule.'
                }
              />
            </dl>
            <Link
              to={`/agreements/${agreement.id}`}
              className="mt-2 inline-flex min-h-11 desk:min-h-9 items-center text-sm font-medium underline"
            >
              Open the arrangement
            </Link>
          </Card>
        </div>

        {/* The change flow: down the right of the desk, in the middle of the phone. */}
        <div className="min-w-0 xl:col-start-2 xl:row-span-2 xl:row-start-1">
          {waiting ? (
            <WaitingPanel
              change={waiting}
              clientId={client.id}
              contacts={contacts}
              onReload={onReload}
            />
          ) : callOffOpen && choosing === null ? (
            <CallOffCard
              visitId={visit.id}
              today={me.today}
              crew={onTheCrew(cleaners)}
              callOff={callOff}
              open={false}
              onChoose={setChoosing}
              onReload={onReload}
            />
          ) : OPEN_STATUSES.has(visit.status) ? (
            <>
              {callOffOpen ? null : (
                <CallOffCard
                  visitId={visit.id}
                  today={me.today}
                  crew={onTheCrew(cleaners)}
                  callOff={callOff}
                  open={visit.status === 'planned' || visit.status === 'confirmed'}
                  onChoose={setChoosing}
                  onReload={onReload}
                />
              )}
              <MoveFlow
                key={`${visit.id}-${changes.length}-${choosing?.kind ?? 'any'}`}
                me={me}
                visit={visit}
                clientId={client.id}
                contacts={contacts}
                onChanged={onChanged}
                {...(callOffOpen && choosing !== null ? { callOff: choosing } : {})}
              />
            </>
          ) : (
            <Card className="mb-6 md:mb-0">
              <h2 className="mb-2 font-serif text-lg">Change this visit</h2>
              <p className="text-sm">
                This visit is {humanise(visit.status).toLowerCase()}, so it cannot be changed. Book
                a new one from the arrangement instead.
              </p>
            </Card>
          )}
        </div>

        {/* What has already happened to it. */}
        <div className="min-w-0 xl:col-start-1 xl:row-start-2">
          <p className="mb-4 text-sm">
            <Link
              to="/issues"
              className="inline-flex min-h-11 desk:min-h-9 items-center font-medium underline"
            >
              Issues: anything that went wrong at a house
            </Link>
          </p>

          <h2 className="mb-2 font-serif text-xl">What has changed on this visit</h2>
          {changes.length === 0 ? (
            <Card>
              <p className="text-sm text-navy/70">
                Nothing yet. This visit is where it was first put.
              </p>
            </Card>
          ) : (
            <ul className="space-y-3">
              {changes.map((change) => (
                <li key={change.id}>
                  {/* The waiting one keeps its controls in the panel above rather than here, so
                      one change never has two of the same form and two of the same field id. */}
                  <ChangeCard
                    change={change}
                    me={me}
                    contacts={contacts}
                    onReload={onReload}
                    resolvable={change.id !== waiting?.id}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
